import React, { useState } from 'react'
import { strings } from '@constants'
import { Chip, Avatar } from '@base'
import { showDateFormat } from '@utils'
import {
	CardContainer,
	CardHeader,
	TitleRow,
	MeetingIcon,
	Title,
	HeaderActions,
	InfoRow,
	InfoText,
	MeetingLink,
	LinkRow,
	CopyButton,
	DateTimeRow,
	AttendeesRow,
	AvatarGroup,
	OverflowCount,
	AttendeeCount,
	MoreButton,
} from './styles'
import { FileCopy, CalendarToday, Schedule, People, LocationOn, Phone, Videocam, MoreHoriz } from '@material-ui/icons'

const MEETING_TYPES = {
	VIDEO_CALL: 'VIDEO_CALL',
	IN_PERSON: 'IN_PERSON',
	PHONE_CALL: 'PHONE_CALL',
}

const TYPE_LABELS = {
	[MEETING_TYPES.VIDEO_CALL]: () => strings(['video', 'call']),
	[MEETING_TYPES.IN_PERSON]: () => strings('in_person'),
	[MEETING_TYPES.PHONE_CALL]: () => strings(['phone', 'call']),
}

const TYPE_COLORS = {
	[MEETING_TYPES.VIDEO_CALL]: { color: '#1967D2', background: '#E8F0FE' },
	[MEETING_TYPES.IN_PERSON]: { color: '#B45309', background: '#FEF3C7' },
	[MEETING_TYPES.PHONE_CALL]: { color: '#15803D', background: '#DCFCE7' },
}

const MAX_VISIBLE_AVATARS = 3

const MeetingCard = ({
	title,
	type = MEETING_TYPES.VIDEO_CALL,
	icon,
	meetingLink,
	meetingLinkLabel,
	location,
	phoneNumber,
	startTime,
	endTime,
	attendees = [],
	onClick,
	children,
}) => {
	const [copied, setCopied] = useState(false)

	const handleCopy = (e, text) => {
		e.stopPropagation()
		navigator.clipboard.writeText(text)
		setCopied(true)
		setTimeout(() => setCopied(false), 2000)
	}

	const isEmail = (str) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(str)

	const getAttendeeLabel = (attendee) => {
		const value = typeof attendee === 'string' ? attendee : attendee?.name || ''
		if (isEmail(value)) return value[0].toUpperCase()
		return value.slice(0, 2)
	}

	const visibleAttendees = attendees.slice(0, MAX_VISIBLE_AVATARS)
	const overflowCount = attendees.length - MAX_VISIBLE_AVATARS

	const renderTypeIcon = () => {
		if (icon) return <MeetingIcon>{icon}</MeetingIcon>
		switch (type) {
			case MEETING_TYPES.VIDEO_CALL:
				return (
					<MeetingIcon>
						<Videocam />
					</MeetingIcon>
				)
			case MEETING_TYPES.IN_PERSON:
				return (
					<MeetingIcon>
						<LocationOn />
					</MeetingIcon>
				)
			case MEETING_TYPES.PHONE_CALL:
				return (
					<MeetingIcon>
						<Phone />
					</MeetingIcon>
				)
			default:
				return null
		}
	}

	const renderDetail = () => {
		switch (type) {
			case MEETING_TYPES.VIDEO_CALL:
				return meetingLink ? (
					<>
						<MeetingLink href={meetingLink} target='_blank' rel='noopener noreferrer' onClick={(e) => e.stopPropagation()}>
							{meetingLinkLabel || strings(['join', 'with', 'google_meet'])}
						</MeetingLink>
						<LinkRow>
							<InfoText>{meetingLink.replace(/^https?:\/\//, '')}</InfoText>
							<CopyButton onClick={(e) => handleCopy(e, meetingLink)} title={copied ? strings('copied') : strings('copy')}>
								<FileCopy fontSize='small' />
							</CopyButton>
						</LinkRow>
					</>
				) : null
			case MEETING_TYPES.IN_PERSON:
				return location ? (
					<InfoRow>
						<LocationOn />
						<InfoText>{location}</InfoText>
					</InfoRow>
				) : null
			case MEETING_TYPES.PHONE_CALL:
				return phoneNumber ? (
					<InfoRow>
						<Phone />
						<InfoText>{phoneNumber}</InfoText>
					</InfoRow>
				) : null
			default:
				return null
		}
	}

	return (
		<CardContainer onClick={onClick}>
			<CardHeader>
				<TitleRow>
					{renderTypeIcon()}
					<Title>{title}</Title>
				</TitleRow>
				<HeaderActions>
					<Chip
						label={TYPE_LABELS[type]?.() || type}
						status='custom'
						color={TYPE_COLORS[type]?.color}
						background={TYPE_COLORS[type]?.background}
						uppercase={false}
						fontMedium
					/>
					<MoreButton onClick={(e) => e.stopPropagation()}>
						<MoreHoriz />
					</MoreButton>
				</HeaderActions>
			</CardHeader>

			{renderDetail()}

			<DateTimeRow>
				{startTime && (
					<InfoRow>
						<CalendarToday />
						<InfoText>{showDateFormat(new Date(startTime))}</InfoText>
					</InfoRow>
				)}
				{startTime && endTime && (
					<InfoRow>
						<Schedule />
						<InfoText>
							{new Date(startTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
							{' - '}
							{new Date(endTime).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: true })}
						</InfoText>
					</InfoRow>
				)}
			</DateTimeRow>

			{attendees.length > 0 && (
				<AttendeesRow>
					<People style={{ fontSize: '1.125rem', color: '#9c9c9c' }} />
					<AvatarGroup>
						{visibleAttendees.map((attendee, index) => (
							<Avatar
								key={index}
								index={index}
								title={typeof attendee === 'string' ? attendee : attendee?.name}
								style={index !== 0 ? { marginLeft: '-0.5rem' } : undefined}
							>
								{getAttendeeLabel(attendee)}
							</Avatar>
						))}
						{overflowCount > 0 && <OverflowCount>+{overflowCount}</OverflowCount>}
					</AvatarGroup>
					<AttendeeCount>
						{attendees.length} {strings('attendees')}
					</AttendeeCount>
				</AttendeesRow>
			)}

			{children}
		</CardContainer>
	)
}

export { MEETING_TYPES }
export default MeetingCard
